import { memo } from "react";
import { Alert } from "@mui/material";
import type { Patient } from "fhir/r5";
import type {
  EcommerceListAudience,
  EcommercePeopleRow,
  DocumentTransactionRow,
  UseEcommerceOrganizationPersonsResult,
} from "src-next/modules/ecommerce";
import EcommercePatientsTable from "./EcommercePatientsTable";
import EcommercePeopleTable from "./EcommercePeopleTable";
import EcommercePeopleListSkeleton from "./EcommercePeopleListSkeleton";

type EcommerceListBodyProps = {
  audience: EcommerceListAudience;
  orgPersons: UseEcommerceOrganizationPersonsResult;
  consentPatientCount: number;
  peopleRows: EcommercePeopleRow[];
  peopleSelectedKeys: string[];
  onTogglePeopleRow: (key: string) => void;
  transactionRows: DocumentTransactionRow[];
  transactionSelectedKeys: string[];
  getTransactionRowKey: (row: DocumentTransactionRow) => string;
  onToggleTransactionRow: (key: string) => void;
  showStatusColumn: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onManagePeople: (patient: Patient) => void;
  onManageTransaction: (patient: Patient, transactionId: string) => void;
  onSendClientWhatsApp: (row: EcommercePeopleRow) => void;
  whatsAppDisabled: boolean;
};

export const EcommerceListBody = memo(function EcommerceListBody({
  audience,
  orgPersons,
  consentPatientCount,
  peopleRows,
  peopleSelectedKeys,
  onTogglePeopleRow,
  transactionRows,
  transactionSelectedKeys,
  getTransactionRowKey,
  onToggleTransactionRow,
  showStatusColumn,
  page,
  onPageChange,
  onManagePeople,
  onManageTransaction,
  onSendClientWhatsApp,
  whatsAppDisabled,
}: EcommerceListBodyProps) {
  if (
    audience === "registered_clients" &&
    orgPersons.loading &&
    !orgPersons.hasFetchedOnce
  ) {
    return <EcommercePeopleListSkeleton />;
  }

  if (audience === "consent_patients" && consentPatientCount === 0) {
    return (
      <Alert severity="info">
        Nenhum paciente com consentimento nesta loja.
      </Alert>
    );
  }

  if (audience === "consent_patients" && peopleRows.length === 0) {
    return (
      <Alert severity="info">Nenhum paciente encontrado para a busca.</Alert>
    );
  }

  if (
    audience === "registered_clients" &&
    orgPersons.hasFetchedOnce &&
    orgPersons.persons.length === 0 &&
    !orgPersons.error
  ) {
    return (
      <Alert severity="info">
        Nenhum cliente cadastrado nesta loja. Você pode cadastrar vários de uma
        vez pelo botão no topo da página.
      </Alert>
    );
  }

  if (
    audience === "registered_clients" &&
    peopleRows.length === 0 &&
    !orgPersons.error
  ) {
    return (
      <Alert severity="info">Nenhum cliente encontrado para a busca.</Alert>
    );
  }

  if (
    audience === "registered_clients" &&
    orgPersons.error &&
    orgPersons.persons.length === 0
  ) {
    return null;
  }

  if (audience === "transactions" && transactionRows.length === 0) {
    return (
      <Alert severity="info">
        Nenhuma transação encontrada para a busca ou filtro.
      </Alert>
    );
  }

  if (audience !== "transactions") {
    return (
      <EcommercePeopleTable
        rows={peopleRows}
        selectedKeys={peopleSelectedKeys}
        onToggleRow={onTogglePeopleRow}
        page={page}
        onPageChange={onPageChange}
        onManage={onManagePeople}
        showConsentColumn={audience === "registered_clients"}
        onSendWhatsApp={
          audience === "registered_clients" ? onSendClientWhatsApp : undefined
        }
        whatsAppDisabled={whatsAppDisabled}
      />
    );
  }

  return (
    <EcommercePatientsTable
      rows={transactionRows}
      selectedKeys={transactionSelectedKeys}
      getRowKey={getTransactionRowKey}
      onToggleRow={onToggleTransactionRow}
      page={page}
      onPageChange={onPageChange}
      showStatusColumn={showStatusColumn}
      onManage={onManageTransaction}
    />
  );
});
